import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { MessagingContact, MessagingPurpose } from "@/contracts";

import { messagingContactsQuery, type MessagingContactFilters } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField, TextField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { formatDateTime } from "@/lib/format";
import { useListFilters } from "@/lib/use-list-filters";
import { AddContactDialog } from "./add-contact-dialog";
import { CONSENT_METHOD_LABELS } from "./consent-labels";
import { RevokeConsentDialog } from "./revoke-consent-dialog";

/**
 * The phone numbers a messaging channel may write to, and why it may.
 *
 * Separate from alert rule recipients on purpose. An email address somebody
 * types on their own rule needs no permission beyond that; a phone number
 * does, so numbers live here with a consent record attached and the
 * notification service reads them from nowhere else.
 */
export function ContactsPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<{
    contact: MessagingContact;
    purpose: MessagingPurpose;
  } | null>(null);

  const includeRevoked = filterValue("revoked") === "true";

  const filters: MessagingContactFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(includeRevoked ? { includeRevoked: true } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    page,
  };

  const contacts = useQuery(messagingContactsQuery(client, filters));

  const rows = contacts.data?.data ?? [];
  const active = rows.filter(
    (contact) =>
      (contact.consentMethod !== null && contact.revokedAt === null) ||
      (contact.voiceConsentMethod !== null && contact.voiceRevokedAt === null),
  ).length;
  const columnCount = isSuperAdmin ? 6 : 5;

  return (
    <>
      <PageHeader
        title={t("contactsTitle")}
        description={t("contactsDescription")}
        action={<Button onClick={() => setAdding(true)}>{t("addContact")}</Button>}
      />

      <Alert tone="warn" title={t("whatsappWarnTitle")} className="mb-4">
        {t("whatsappWarnBody")}
      </Alert>

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              label={tCommon("search")}
              placeholder={t("searchContacts")}
              value={filterValue("search")}
              onChange={(event) => setFilter("search", event.target.value)}
            />

            <SelectField
              label={tCommon("consent")}
              value={includeRevoked ? "true" : ""}
              onChange={(value) => setFilter("revoked", value)}
              data={[
                { value: "", label: t("currentContacts") },
                { value: "true", label: t("includeWithdrawn") },
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {contacts.isPending ? <LoadingBlock label={t("loadingContacts")} /> : null}

      {contacts.isError ? (
        <ErrorBlock error={contacts.error} onRetry={() => void contacts.refetch()} />
      ) : null}

      {contacts.data ? (
        <>
          {active === 0 && !includeRevoked ? (
            <Card className="mb-4">
              <CardBody>
                <p className="text-sm">
                  <span className="font-semibold">No number can be messaged.</span> Incidents will
                  still open and appear on the incidents screen, but the messaging channel has
                  nobody to write to.
                </p>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <DataTable caption="Messaging contacts">
              <thead>
                <tr>
                  {isSuperAdmin ? <Th>{tCommon("organization")}</Th> : null}
                  <Th>{tCommon("name")}</Th>
                  <Th>{t("colNumber")}</Th>
                  <Th>{t("colConsent")}</Th>
                  <Th>{t("colRecordedBy")}</Th>
                  <Th>{tCommon("status")}</Th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <EmptyRow colSpan={columnCount}>
                    {filterValue("search")
                      ? t("emptyContactsSearch")
                      : t("emptyContacts")}
                  </EmptyRow>
                ) : null}

                {rows.map((contact) => (
                  <tr key={contact.id}>
                    {isSuperAdmin ? <Td>{contact.organizationName}</Td> : null}
                    <Td className="font-medium">
                      {contact.displayName}
                      {contact.role === null ? null : (
                        <span className="block text-xs text-ink-muted">{contact.role}</span>
                      )}
                    </Td>
                    <Td className="tabular-nums">{contact.phone}</Td>
                    <Td>
                      <ConsentLine
                        channel={t("channelWhatsapp")}
                        method={contact.consentMethod}
                        at={contact.consentedAt}
                        note={contact.consentNote}
                      />
                      <ConsentLine
                        channel={t("channelVoice")}
                        method={contact.voiceConsentMethod}
                        at={contact.voiceConsentedAt}
                        note={contact.voiceConsentNote}
                      />
                    </Td>
                    <Td>
                      {contact.consentRecordedByName ?? contact.voiceConsentRecordedByName ?? "—"}
                    </Td>
                    <Td>
                      <ConsentStatus
                        active={contact.consentMethod !== null && contact.revokedAt === null}
                        revokedAt={contact.revokedAt}
                        stopLabel={t("stopWhatsapp")}
                        onStop={() => setRevoking({ contact, purpose: "WHATSAPP" })}
                      />
                      <ConsentStatus
                        active={
                          contact.voiceConsentMethod !== null && contact.voiceRevokedAt === null
                        }
                        revokedAt={contact.voiceRevokedAt}
                        stopLabel={t("stopVoice")}
                        onStop={() => setRevoking({ contact, purpose: "VOICE" })}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr>
                  <Td colSpan={columnCount} className="border-b-0">
                    <Pagination
                      page={contacts.data.pagination.page}
                      totalPages={contacts.data.pagination.totalPages}
                      total={contacts.data.pagination.total}
                      noun={{ one: tCommon("noun.contact.one"), other: tCommon("noun.contact.other") }}
                      onChange={setPage}
                    />
                  </Td>
                </tr>
              </tfoot>
            </DataTable>
          </Card>
        </>
      ) : null}

      {adding ? <AddContactDialog onClose={() => setAdding(false)} /> : null}

      {revoking ? (
        <RevokeConsentDialog
          contact={revoking.contact}
          purpose={revoking.purpose}
          onClose={() => setRevoking(null)}
        />
      ) : null}
    </>
  );
}

function ConsentLine({
  channel,
  method,
  at,
  note,
}: {
  channel: string;
  method: MessagingContact["consentMethod"];
  at: string | null;
  note: string | null;
}) {
  if (method === null || at === null) {
    return null;
  }

  return (
    <div className="mb-2 last:mb-0">
      <span className="block text-xs font-medium text-ink-muted">{channel}</span>
      {CONSENT_METHOD_LABELS[method]}
      <span className="block text-xs text-ink-muted">{formatDateTime(at)}</span>
      {note === null ? null : <span className="block text-xs text-ink-muted">{note}</span>}
    </div>
  );
}

function ConsentStatus({
  active,
  revokedAt,
  stopLabel,
  onStop,
}: {
  active: boolean;
  revokedAt: string | null;
  stopLabel: string;
  onStop: () => void;
}) {
  const { t } = useTranslation("pages");

  if (active) {
    return (
      <div className="mb-2 flex items-center gap-2 last:mb-0">
        <Badge tone="ok">{t("consented")}</Badge>
        <Button variant="ghost" onClick={onStop}>
          {stopLabel}
        </Button>
      </div>
    );
  }

  if (revokedAt === null) {
    return null;
  }

  return (
    <div className="mb-2 last:mb-0">
      <Badge tone="neutral">{t("withdrawn")}</Badge>
      <span className="block text-xs text-ink-muted">{formatDateTime(revokedAt)}</span>
    </div>
  );
}
